import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/encaissements';
import { fmtEur, fmtDateHeure } from './EncaissementModal';

export const STATUT_BADGE = {
  brouillon:      { label: 'Brouillon', classe: 'status-info' },
  en_attente_tpe: { label: 'Sur le TPE…', classe: 'status-warning' },
  lien_envoye:    { label: 'Lien envoyé', classe: 'status-info' },
  paye:           { label: 'Payé', classe: 'status-success' },
  refuse:         { label: 'Refusé', classe: 'status-error' },
  annule:         { label: 'Annulé', classe: 'status-error' },
  expire:         { label: 'Expiré', classe: 'status-error' },
  a_verifier:     { label: 'À vérifier', classe: 'status-warning' },
};

const LIBELLE_EVENT = {
  cree: 'Encaissement créé', modifie: 'Brouillon modifié', tpe_envoye: 'Montant envoyé sur le terminal',
  tpe_sans_reponse: 'Pas de réponse du terminal', paye: 'Paiement accepté', tpe_refuse: 'Paiement refusé',
  tpe_annule: 'Transaction annulée', tpe_brouillon: 'Terminal occupé — retour en brouillon', tpe_a_verifier: 'Terminal injoignable — à vérifier',
  abandon_demande: 'Abandon demandé au terminal', tpe_en_cours: 'Transaction toujours en cours (poller)', tpe_introuvable: 'Transaction jamais reçue par le terminal',
  autorisation_partielle: 'Autorisation PARTIELLE', lien_cree: 'Lien de paiement créé', sms_envoye: 'SMS envoyé', sms_echec: 'SMS non envoyé',
  email_envoye: 'E-mail envoyé', email_echec: 'E-mail non envoyé', lien_revoque: 'Lien révoqué', lien_expire: 'Lien expiré', lien_revocation_echec: 'Révocation du lien échouée',
  confirmation_webhook: 'Confirmation Adyen (webhook)', paye_incoherent: '⚠️ Paiement reçu sur un encaissement clos', montant_different: '⚠️ Montant encaissé différent',
  double_paiement: '⚠️ Second paiement reçu', autorisation_refusee: 'Autorisation refusée (Adyen)', annule: 'Annulé', reouvert: 'Rouvert', resolu_manuellement: 'Résolu manuellement',
  resultat_ignore: 'Résultat ignoré (déjà conclu)', refund: '⚠️ Remboursement (Adyen)', cancellation: '⚠️ Annulation (Adyen)', chargeback: '⚠️ Contestation (chargeback)',
};
const SOURCE = { crm: 'CRM', adyen_sync: 'Terminal', webhook: 'Webhook', poller: 'Poller' };

function EncaissementDetail({ user, encaissement: initial, onClose, onChange, onRelancer }) {
  const [enc, setEnc] = useState(initial);
  const [events, setEvents] = useState([]);
  const [liens, setLiens] = useState([]);
  const [erreur, setErreur] = useState('');
  const [busy, setBusy] = useState(false);
  const [resolution, setResolution] = useState(null);
  const timer = useRef(null);
  const isSuperAdmin = user?.role === 'super-admin';

  const charger = useCallback(async () => {
    try {
      const [{ data: e }, { data: ev }] = await Promise.all([api.lire(initial.id), api.events(initial.id)]);
      setEnc(e.encaissement); setEvents(ev.events || []); setLiens(ev.liens || []);
      onChange && onChange(e.encaissement);
      return e.encaissement;
    } catch (err) { setErreur(err.response?.data?.error || err.message); return null; }
  }, [initial.id, onChange]);

  useEffect(() => { charger(); }, [charger]);
  // Tant qu'une transaction terminal est en cours, on suit.
  useEffect(() => {
    if (enc?.statut !== 'en_attente_tpe') return undefined;
    timer.current = setTimeout(charger, 2000);
    return () => clearTimeout(timer.current);
  }, [enc, charger]);

  const action = async (fn) => {
    setErreur(''); setBusy(true);
    try { await fn(); await charger(); }
    catch (err) { setErreur(err.response?.data?.error || err.message); }
    finally { setBusy(false); }
  };

  if (!enc) return null;
  const badge = STATUT_BADGE[enc.statut] || { label: enc.statut, classe: 'status-info' };
  const lienCourant = liens.length ? liens[liens.length - 1] : null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>💳 {enc.reference} — {fmtEur(enc.montant_cents)} <span className={badge.classe} style={{ marginLeft: 10, fontSize: 13 }}>{badge.label}</span>{enc.alerte && <span className="status-warning" style={{ marginLeft: 6, fontSize: 13 }}>⚠️ alerte</span>}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {enc.statut === 'a_verifier' && (
            <div className="status-warning enc-bandeau" style={{ marginBottom: 12 }}>
              <b>À vérifier.</b> {enc.refus_motif || 'Le résultat de la transaction est inconnu.'} Le CRM interroge Adyen toutes les 2 minutes.
              {isSuperAdmin && ' Si le paiement est visible dans la Customer Area Adyen, résolvez-le manuellement ci-dessous.'}
            </div>
          )}
          {enc.alerte && enc.statut !== 'a_verifier' && enc.refus_motif && (
            <div className="status-warning enc-bandeau" style={{ marginBottom: 12 }}>{enc.refus_motif}</div>
          )}
          <dl className="enc-detail-grid">
            <div><dt>Client</dt><dd>{enc.client_prenom} {enc.client_nom}{enc.client_code ? <span className="enc-ref"> · code {enc.client_code}</span> : ''}</dd></div>
            <div><dt>Contact</dt><dd>{enc.client_telephone || '—'}{enc.client_email ? ` · ${enc.client_email}` : ''}</dd></div>
            <div><dt>Référence</dt><dd>{enc.numero_bdc ? `BDC ${enc.numero_bdc}` : ''}{enc.numero_bdc && enc.numero_facture ? ' · ' : ''}{enc.numero_facture ? `Facture ${enc.numero_facture}` : ''}</dd></div>
            <div><dt>Agence · société</dt><dd>{enc.concession_nom} · {enc.codsoc}</dd></div>
            <div><dt>Vendeur</dt><dd>{[enc.vendeur_prenom, enc.vendeur_nom].filter(Boolean).join(' ') || '—'}</dd></div>
            <div><dt>Créé le</dt><dd>{fmtDateHeure(enc.created_at)}</dd></div>
            <div><dt>Canal</dt><dd>{enc.canal === 'terminal' ? `TPE ${enc.terminal_libelle || ''} (${enc.poiid || '—'})` : enc.canal === 'lien' ? 'Lien de paiement' : '—'}</dd></div>
            <div><dt>Payé le</dt><dd>{fmtDateHeure(enc.paye_le)}{enc.montant_autorise_cents && enc.montant_autorise_cents !== enc.montant_cents ? ` — encaissé ${fmtEur(enc.montant_autorise_cents)}` : ''}</dd></div>
            <div><dt>Référence Adyen (pspReference)</dt><dd className="enc-ref">{enc.psp_reference || '—'}{enc.tender_reference ? ` · tender ${enc.tender_reference}` : ''}</dd></div>
            <div><dt>Carte</dt><dd>{enc.carte_resume ? `****${enc.carte_resume}` : '—'}{enc.moyen_paiement ? ` (${enc.moyen_paiement})` : ''}</dd></div>
            {enc.commentaire && <div className="full" style={{ gridColumn: '1 / -1' }}><dt>Commentaire</dt><dd>{enc.commentaire}</dd></div>}
          </dl>

          {enc.statut === 'lien_envoye' && lienCourant && (
            <div className="enc-lien-url">
              <input readOnly value={lienCourant.url} onFocus={(e) => e.target.select()} style={{ padding: 8, borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)' }} />
              <button className="btn-secondary" onClick={() => { try { navigator.clipboard.writeText(lienCourant.url); } catch { /* sans presse-papier */ } }}>Copier</button>
              <small className="enc-hint" style={{ margin: 0 }}>expire le {fmtDateHeure(lienCourant.expire_le)}</small>
            </div>
          )}

          <div className="enc-actions">
            {enc.statut === 'brouillon' && <button className="btn-primary" disabled={busy} onClick={() => onRelancer(enc)}>Envoyer (TPE ou lien)</button>}
            {['refuse', 'annule', 'expire'].includes(enc.statut) && (
              <button className="btn-primary" disabled={busy} onClick={() => action(async () => { const { data } = await api.reouvrir(enc.id); onRelancer(data.encaissement); })}>Relancer</button>
            )}
            {enc.statut === 'lien_envoye' && <button className="btn-secondary" disabled={busy} onClick={() => onRelancer(enc)}>Renvoyer un lien</button>}
            {['brouillon', 'en_attente_tpe', 'lien_envoye', 'refuse', 'expire'].includes(enc.statut) && (
              <button className="btn-secondary" disabled={busy} onClick={() => {
                const q = enc.statut === 'en_attente_tpe' ? 'Demander au terminal d\'abandonner la transaction ?' : enc.statut === 'lien_envoye' ? 'Révoquer le lien de paiement ? Le client ne pourra plus payer avec.' : 'Annuler cet encaissement ?';
                if (window.confirm(q)) action(() => api.annuler(enc.id));
              }}>{enc.statut === 'en_attente_tpe' ? 'Abandonner la transaction' : enc.statut === 'lien_envoye' ? 'Révoquer le lien' : 'Annuler'}</button>
            )}
            {isSuperAdmin && ['a_verifier', 'en_attente_tpe'].includes(enc.statut) && (
              <button className="btn-secondary" disabled={busy} onClick={() => setResolution({ statut: 'paye', motif: '', psp_reference: enc.psp_reference || '' })}>Résoudre manuellement</button>
            )}
          </div>

          {resolution && (
            <div className="enc-bandeau" style={{ border: '1px solid var(--border-default)', marginBottom: 12 }}>
              <b>Résolution manuelle</b> — après contrôle dans la Customer Area Adyen.
              <div className="enc-config-form" style={{ marginTop: 8 }}>
                <div><label>Issue</label>
                  <select value={resolution.statut} onChange={(e) => setResolution((r) => ({ ...r, statut: e.target.value }))}>
                    <option value="paye">Payé (visible dans Adyen)</option><option value="annule">Annulé (rien n'a été encaissé)</option>
                  </select></div>
                {resolution.statut === 'paye' && <div><label>pspReference</label><input value={resolution.psp_reference} onChange={(e) => setResolution((r) => ({ ...r, psp_reference: e.target.value }))} /></div>}
                <div style={{ flex: 1 }}><label>Motif *</label><input value={resolution.motif} onChange={(e) => setResolution((r) => ({ ...r, motif: e.target.value }))} placeholder="ex. paiement retrouvé dans Adyen le 22/09" /></div>
                <button className="btn-primary" disabled={busy || !resolution.motif.trim()} onClick={() => action(async () => { await api.resoudre(enc.id, resolution); setResolution(null); })}>Valider</button>
                <button className="btn-secondary" onClick={() => setResolution(null)}>Annuler</button>
              </div>
            </div>
          )}

          {erreur && <div className="status-error enc-bandeau" style={{ marginBottom: 12 }}>{erreur}</div>}

          <h3 style={{ fontSize: 14, margin: '8px 0 0' }}>Journal</h3>
          <ul className="enc-journal">
            {events.map((ev) => (
              <li key={ev.id}>
                <span className="enc-j-date">{fmtDateHeure(ev.created_at)}</span>
                <span className="enc-j-source">{SOURCE[ev.source] || ev.source}{ev.user_prenom ? ` · ${ev.user_prenom}` : ''}</span>
                <span>
                  {LIBELLE_EVENT[ev.type] || ev.type}
                  {ev.detail && (ev.detail.motif || ev.detail.erreur || ev.detail.refusalReason || ev.detail.message) && (
                    <code>{ev.detail.motif || ev.detail.erreur || ev.detail.refusalReason || ev.detail.message}</code>
                  )}
                </span>
              </li>
            ))}
            {!events.length && <li><span /><span /><span style={{ color: 'var(--text-muted)' }}>Aucun événement.</span></li>}
          </ul>
        </div>
        <div className="modal-footer"><button className="btn-secondary" onClick={onClose}>Fermer</button></div>
      </div>
    </div>
  );
}

export default EncaissementDetail;
